import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import Sidebar from './components/Sidebar'
import Header from './components/Header'
import PageTitle from './components/PageTitle'
import Analytics from './components/Analytics'
import Home from './components/Home'
import './App.css'

const GasChoke = lazy(() => import('./components/calculators/GasChoke'))
const GasVelocity = lazy(() => import('./components/calculators/GasVelocity'))
const GOR2Computation = lazy(() => import('./components/calculators/GOR2Computation'))
const CombinedMeterFactor = lazy(() => import('./components/calculators/CombinedMeterFactor'))
const KatzShrinkage = lazy(() => import('./components/calculators/KatzShrinkage'))
const Shrinkage = lazy(() => import('./components/calculators/Shrinkage'))
const SpecificGravity = lazy(() => import('./components/calculators/SpecificGravity'))
const VolumeCorrectionFactor = lazy(() => import('./components/calculators/VolumeCorrectionFactor'))
const PressureConverter = lazy(() => import('./components/calculators/PressureConverter'))
const TemperatureConverter = lazy(() => import('./components/calculators/TemperatureConverter'))
const VolumeConverter = lazy(() => import('./components/calculators/VolumeConverter'))
const Density = lazy(() => import('./components/calculators/Density'))
const KillFluid = lazy(() => import('./components/calculators/KillFluid'))
const ZFactor = lazy(() => import('./components/calculators/ZFactor'))
const APIKeys = lazy(() => import('./components/APIKeys'))
const APIDocs = lazy(() => import('./components/APIDocs'))
const APITester = lazy(() => import('./components/APITester'))
const SavedCalculations = lazy(() => import('./components/SavedCalculations'))
const Dashboard = lazy(() => import('./components/Dashboard'))
const Subscription = lazy(() => import('./components/Subscription'))
const Blog = lazy(() => import('./components/Blog'))
const BlogPost = lazy(() => import('./components/BlogPost'))

const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    fontSize: '18px',
    color: '#666'
  }}>
    Loading...
  </div>
)

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <PageTitle />
          <Analytics />
          <div className="app">
            <Header />
            <div className="app-container">
              <Sidebar />
              <main className="main-content">
                <div className="calculators-container">
                  <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/calculators/gas-choke" element={<GasChoke />} />
                      <Route path="/calculators/gas-velocity" element={<GasVelocity />} />
                      <Route path="/calculators/gor2" element={<GOR2Computation />} />
                      <Route path="/calculators/combined-meter" element={<CombinedMeterFactor />} />
                      <Route path="/calculators/katz-shrinkage" element={<KatzShrinkage />} />
                      <Route path="/calculators/shrinkage" element={<Shrinkage />} />
                      <Route path="/calculators/specific-gravity" element={<SpecificGravity />} />
                      <Route path="/calculators/volume-correction" element={<VolumeCorrectionFactor />} />
                      <Route path="/converters/pressure" element={<PressureConverter />} />
                      <Route path="/converters/temperature" element={<TemperatureConverter />} />
                      <Route path="/converters/volume" element={<VolumeConverter />} />
                      <Route path="/converters/density" element={<Density />} />
                      <Route path="/calculators/kill-fluid" element={<KillFluid />} />
                      <Route path="/calculators/z-factor" element={<ZFactor />} />
                      <Route path="/saved" element={<SavedCalculations />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/subscription" element={<Subscription />} />
                      <Route path="/api/keys" element={<APIKeys />} />
                      <Route path="/api/docs" element={<APIDocs />} />
                      <Route path="/api/test" element={<APITester />} />
                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog/:slug" element={<BlogPost />} />
                      <Route path="/blog/category/:categorySlug" element={<Blog />} />
                    </Routes>
                  </Suspense>
                </div>
              </main>
          </div>
        </div>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
